import { useState } from 'react';
import {
  useAllotments,
  useCreateAllotment,
  useUpdateAllotment,
  useDeleteAllotment,
} from '../../../../hooks/useAllotments';
import { ITEMS_PER_PAGE, INITIAL_ALLOTMENT_STATE } from '../constants/allotmentsConstants';
import { filterAllotments } from '../utils/allotmentsUtils';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';

export const useAllotmentsTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAllotment, setSelectedAllotment] = useState(INITIAL_ALLOTMENT_STATE);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageError, setImageError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [allotmentToDelete, setAllotmentToDelete] = useState(null);

  const { data: allotments = [], isLoading } = useAllotments();
  const createMutation = useCreateAllotment();
  const updateMutation = useUpdateAllotment();
  const deleteMutation = useDeleteAllotment();

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: allotments,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterAllotments,
  });

  const handleOpenDialog = () => {
    setSelectedAllotment(INITIAL_ALLOTMENT_STATE);
    setEditMode(false);
    setSelectedImageFile(null);
    setImageError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    setSelectedAllotment((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (file) => {
    setSelectedImageFile(file || null);
    setImageError('');
  };

  const handleSave = () => {
    if (!editMode && !selectedImageFile) {
      setImageError('Debe seleccionar una imagen.');
      return;
    }
    if (editMode && !selectedImageFile && !selectedAllotment?.image_url) {
      setImageError('Debe seleccionar una imagen.');
      return;
    }

    const formData = new FormData();
    if (selectedAllotment?.name) {
      formData.append('name', selectedAllotment.name);
    }
    if (typeof selectedAllotment?.is_active === 'boolean') {
      formData.append('is_active', selectedAllotment.is_active ? 'true' : 'false');
    }
    if (selectedImageFile) {
      formData.append('image', selectedImageFile);
    }

    if (editMode) {
      updateMutation.mutate(
        { id: selectedAllotment.uuid, allotmentData: formData },
        {
          onSuccess: () => {
            handleCloseDialog();
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          handleCloseDialog();
        },
      });
    }
  };

  const handleEdit = (allotment) => {
    setSelectedAllotment(allotment);
    setEditMode(true);
    setSelectedImageFile(null);
    setImageError('');
    setDialogOpen(true);
  };

  const handleDelete = (allotment) => {
    setAllotmentToDelete(allotment);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (allotmentToDelete) {
      try {
        await deleteMutation.mutateAsync(allotmentToDelete.uuid);
        setDeleteConfirmOpen(false);
        setAllotmentToDelete(null);
      } catch (error) {
        console.error('Error deleting allotment:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setAllotmentToDelete(null);
  };

  return {
    data: paginatedData,
    isLoading:
      isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedAllotment,
    selectedImageFile,
    imageError,
    deleteConfirmOpen,
    allotmentToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleImageChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
